<template>
  <!-- Групповое изменение ед. измерения выбранных материалов («Базовые операции»), как material-group-measure-form
       в smremont: меняются только поля с отметкой «Изменить». -->
  <div class="ms-form">
    <q-form @submit="onSubmit">
      <div class="measure-all">
        <CustomCheckbox label="Изменить все" :value="allChecked" @change="toggleAll" />
      </div>

      <section class="ms-form-section">
        <h3 class="ms-form-section__title"><i class="fa-solid fa-ruler" aria-hidden="true" />Единицы измерения</h3>
        <div class="ms-form-grid">
          <div v-for="field in unitFields" :key="field.key" class="measure-field">
            <CustomSelect
              :label="field.label"
              :options="units"
              option-value-key="unit_id"
              option-label-key="unit_name"
              :disabled="!edit[field.key]"
              :value="form[field.key]"
              @change="(v) => (form[field.key] = v || null)"
            />
            <CustomCheckbox label="Изменить" :value="edit[field.key]" @change="(v) => (edit[field.key] = !!v)" />
          </div>
        </div>
      </section>

      <section class="ms-form-section">
        <h3 class="ms-form-section__title"><i class="fa-solid fa-calculator" aria-hidden="true" />Коэффициенты</h3>
        <div class="ms-form-grid">
          <div v-for="field in numberFields" :key="field.key" class="measure-field">
            <CustomInput
              type="number"
              :label="field.label"
              :disable="!edit[field.key]"
              :value="form[field.key]"
              @change_input="(v) => (form[field.key] = v)"
            />
            <CustomCheckbox label="Изменить" :value="edit[field.key]" @change="(v) => (edit[field.key] = !!v)" />
          </div>
          <div class="measure-field">
            <div>
              <div class="ms-form-label">Округлять отпуск материала</div>
              <div class="ms-seg">
                <button
                  v-for="opt in [{ value: true, label: 'Да' }, { value: false, label: 'Нет' }]"
                  :key="opt.label"
                  type="button"
                  :disabled="!edit.is_koef_round"
                  :class="{ active: form.is_koef_round === opt.value }"
                  @click="form.is_koef_round = opt.value"
                >{{ opt.label }}</button>
              </div>
            </div>
            <CustomCheckbox label="Изменить" :value="edit.is_koef_round" @change="(v) => (edit.is_koef_round = !!v)" />
          </div>
        </div>
      </section>

      <p v-if="changedLabels.length" class="measure-summary">
        Будет изменено у выбранных материалов: {{ changedLabels.join(", ") }}
      </p>
      <p v-else class="ms-empty">Отметьте «Изменить» у полей, которые нужно поменять</p>

      <CustomModalFooter :btn-loading="loading" />
    </q-form>

    <!-- как в smremont: перед сохранением подтверждение со списком изменений -->
    <PopupConfirm
      ref="confirm"
      :target="confirmAnchor || false"
      no-parent-event
      is-default
      :title="confirmTitle"
      :approve="save"
    />
  </div>
</template>

<script>
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import PopupConfirm from "@/components/common/PopupConfirm";
import { materialsAPI } from "@/views/Materials/api";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";

const UNIT_FIELDS = [
  { key: "sell_unit_id", label: "Единица продажи" },
  { key: "consume_unit_id", label: "Единица расхода" },
  { key: "delivery_unit_id", label: "Единица отпуска из магазина" },
  { key: "atom_unit_id", label: "Минимальная единица" },
  { key: "pack_unit_id", label: "Единица в одной упаковке" },
];
const NUMBER_FIELDS = [
  { key: "koef_sell_to_delivery", label: "Ед. продажи в одной ед. отпуска" },
  { key: "koef_consume_to_delivery", label: "Ед. расхода в одной ед. отпуска" },
  { key: "koef_atom_to_delivery", label: "Мин. ед. в одной ед. отпуска" },
  { key: "koef_atom_to_pack", label: "Мин. ед. в одной упаковке" },
  { key: "consumption", label: "Коэффициент расхода" },
];
const ALL_KEYS = [...UNIT_FIELDS, ...NUMBER_FIELDS].map((f) => f.key).concat("is_koef_round");

export default {
  name: "MaterialGroupMeasure",
  components: { CustomModalFooter, CustomInput, CustomSelect, CustomCheckbox, PopupConfirm },
  props: ["data"],
  data() {
    return {
      loading: false,
      confirmAnchor: null,
      units: [],
      unitFields: UNIT_FIELDS,
      numberFields: NUMBER_FIELDS,
      form: {
        ...Object.fromEntries([...UNIT_FIELDS, ...NUMBER_FIELDS].map((f) => [f.key, null])),
        is_koef_round: false,
      },
      edit: Object.fromEntries(ALL_KEYS.map((key) => [key, false])),
    };
  },
  computed: {
    allChecked() {
      return ALL_KEYS.every((key) => this.edit[key]);
    },
    changedLabels() {
      const labels = Object.fromEntries([...UNIT_FIELDS, ...NUMBER_FIELDS].map((f) => [f.key, f.label]));
      labels.is_koef_round = "Округление отпуска";
      return ALL_KEYS.filter((key) => this.edit[key]).map((key) => labels[key]);
    },
    confirmTitle() {
      const count = this.data?.body?.material_ids?.length || 0;
      return `Изменить ед. измерения выбранных материалов (${count})? Изменится: ${this.changedLabels.join(", ")}`;
    },
  },
  async mounted() {
    // ошибку уже показал тостом перехватчик axios
    const res = await materialsAPI.getMaterialUnits().catch(() => undefined);
    this.units = res?.data || [];
  },
  methods: {
    toggleAll(value) {
      ALL_KEYS.forEach((key) => (this.edit[key] = !!value));
    },
    onSubmit() {
      if (!this.changedLabels.length) {
        this.showWarningSnackbar("Для сохранения необходимо внести изменения");
        return;
      }
      this.confirmAnchor = this.$el.querySelector('button[type="submit"]');
      this.$nextTick(() => this.$refs.confirm?.$refs.popupProxy?.show());
    },
    async save() {
      const body = { material_ids: this.data?.body?.material_ids || [], ...this.form };
      ALL_KEYS.forEach((key) => (body[`is_${key}`] = this.edit[key] ? 1 : 0));
      this.loading = true;
      try {
        await this.data?.onSubmit(body);
      } finally {
        this.loading = false;
      }
    },
  },
  setup() {
    const { showWarningSnackbar } = useCustomSnackbar();
    return { showWarningSnackbar };
  },
};
</script>

<style scoped lang="scss">
.measure-all {
  display: flex;
  justify-content: flex-end;
}

.measure-field {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  min-width: 0;

  > :first-child {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.ms-seg button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.measure-summary {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--ms-text-secondary);
}
</style>
